import { createFileRoute, Link } from "@tanstack/react-router";
import prima1 from "@/assets/prima1.webp.asset.json";
import dopo1 from "@/assets/dopo1.webp.asset.json";
import prima2 from "@/assets/prima2.webp.asset.json";
import dopo2 from "@/assets/dopo2.webp.asset.json";
import prima3 from "@/assets/prima3.webp.asset.json";
import dopo3 from "@/assets/dopo3.webp.asset.json";
import libera6 from "@/assets/libera6.webp.asset.json";
import libera7 from "@/assets/libera7.webp.asset.json";
import libera8 from "@/assets/libera8.webp.asset.json";
import { BeforeAfter } from "@/components/BeforeAfter";
import { PreventivoForm } from "@/components/PreventivoForm";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SocialLinks } from "@/components/Social";
import {
  ANNI_ESPERIENZA,
  ANNO_FONDAZIONE,
  EMAIL,
  TEL_ANTONIO,
  TEL_LUCA,
  WHATSAPP_URL,
  province,
} from "@/lib/site";

const URL_PAGINA = "https://artigianidelcotto.com/trattamento-cotto-esterno";
const TITOLO = "Trattamento cotto esterno: terrazze e portici | Lombardia";
const DESCRIZIONE =
  "Lavaggio, protezione antigelo e manutenzione del cotto esterno: terrazze, cortili, scale e portici. Artigiani dal 1990, preventivo gratuito: 339 751 7813.";

const interventi = [
  {
    titolo: "Lavaggio e rimozione di muschi ed efflorescenze",
    testo:
      "Togliamo muschio, alghe, sali affiorati (efflorescenze), residui di posa e macchie di ruggine o foglie con detergenti professionali a base d'acqua e, dove serve, con carteggiatura a dischi abrasivi. Il cotto torna al suo colore vero prima di qualsiasi protezione.",
    voci: ["Muschi e alghe", "Efflorescenze e aloni bianchi", "Macchie di foglie e ruggine", "Residui di posa e cemento"],
  },
  {
    titolo: "Protezione idrorepellente traspirante",
    testo:
      "In esterno non si usa la cera: applichiamo un protettivo idro-oleorepellente che lascia traspirare la mattonella. L'acqua non penetra, quindi non gela dentro il cotto e non lo sfalda, ma il vapore continua a uscire. Finitura naturale opaca o effetto bagnato, a scelta.",
    voci: ["Antigelo per gli inverni lombardi", "Effetto naturale o bagnato", "Superficie non scivolosa", "Prodotti non nocivi per piante e animali"],
  },
  {
    titolo: "Manutenzione periodica",
    testo:
      "All'aperto il protettivo si consuma più in fretta che in casa. Con un lavaggio e un rinnovo del prodotto ogni pochi anni la superficie resta protetta, senza dover mai rifare il pavimento.",
    voci: ["Controllo dello stato del protettivo", "Lavaggio di mantenimento", "Rinnovo della protezione", "Interventi programmati"],
  },
];

const passaggi = [
  {
    titolo: "Sopralluogo gratuito",
    testo: "Valutiamo lo stato del cotto, l'esposizione e i metri quadri. Preventivo scritto, senza impegno.",
  },
  {
    titolo: "Lavaggio profondo",
    testo: "Rimozione di muschi, efflorescenze e vecchi trattamenti sfogliati con prodotti dedicati.",
  },
  {
    titolo: "Asciugatura completa",
    testo: "Il cotto esterno deve essere asciutto in profondità: solo così il protettivo penetra e dura.",
  },
  {
    titolo: "Protezione e finitura",
    testo: "Applicazione del protettivo traspirante, a più mani, con la finitura concordata.",
  },
];

const lavoriEsterni = [
  { label: "Cortile in cotto lavato e protetto", before: prima1.url, after: dopo1.url },
  { label: "Scala e camminamento esterni riportati a nuovo", before: prima3.url, after: dopo3.url },
  { label: "Terrazza in cotto lavata e trattata", before: prima2.url, after: dopo2.url },
];

const gallerie = [
  { src: libera6.url, alt: "Bordo piscina e camminamento in cotto esterno trattato", w: 900, h: 675 },
  { src: libera7.url, alt: "Terrazzo in cotto con pergolato dopo lavaggio e protezione", w: 675, h: 900 },
  { src: libera8.url, alt: "Portico esterno in cotto rosso pulito e protetto", w: 675, h: 900 },
];


const faq = [
  {
    q: "Quanto dura il trattamento del cotto esterno?",
    a: "In esterno il protettivo lavora sotto pioggia, sole e gelo: in media va rinnovato ogni 3-5 anni, a seconda dell'esposizione e del passaggio. Il lavaggio profondo, invece, si fa una volta sola: dopo bastano le manutenzioni.",
  },
  {
    q: "Il pavimento diventa scivoloso dopo il trattamento?",
    a: "No. In esterno usiamo protettivi che penetrano nel cotto senza creare pellicole lucide: la superficie resta ruvida e calpestabile anche bagnata. La cera, che sarebbe scivolosa, la usiamo solo in interno.",
  },
  {
    q: "Si può trattare il cotto esterno d'inverno?",
    a: "Si può lavorare da primavera ad autunno inoltrato. Servono giornate asciutte e temperature sopra i 5-8 gradi, perché il cotto deve asciugare in profondità prima della protezione. Con il gelo o la pioggia rinviamo l'intervento.",
  },
  {
    q: "Il trattamento protegge dal gelo?",
    a: "Sì, indirettamente ed è il suo compito principale: se l'acqua non entra nella mattonella, non congela e non ne provoca lo sfaldamento. Per questo in Lombardia il protettivo idrorepellente traspirante è la scelta giusta per cortili e terrazze.",
  },
  {
    q: "Quanto costa il trattamento del cotto esterno?",
    a: "Il prezzo dipende dai metri quadri e dallo stato della superficie: un cotto con muschio e vecchi trattamenti richiede più lavoro di uno solo sporco. Sopralluogo e preventivo sono gratuiti; per una prima stima bastano due foto su WhatsApp.",
  },
];

export const Route = createFileRoute("/trattamento-cotto-esterno")({
  component: CottoEsterno,
  head: () => ({
    meta: [
      { title: TITOLO },
      { name: "description", content: DESCRIZIONE },
      { property: "og:title", content: TITOLO },
      { property: "og:description", content: DESCRIZIONE },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL_PAGINA },
      { property: "og:image", content: `https://artigianidelcotto.com${dopo1.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://artigianidelcotto.com${dopo1.url}` },
      { name: "geo.region", content: "IT-25" },
    ],
    links: [{ rel: "canonical", href: URL_PAGINA }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "HomeAndConstructionBusiness",
              "@id": "https://artigianidelcotto.com/#azienda",
              name: "Artigiani del Cotto",
              url: "https://artigianidelcotto.com/",
              telephone: "+39 339 751 7813",
              email: EMAIL,
              image: `https://artigianidelcotto.com${dopo1.url}`,
              foundingDate: String(ANNO_FONDAZIONE),
              priceRange: "€€",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Torbole Casaglia",
                addressRegion: "BS",
                addressCountry: "IT",
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
            },
            {
              "@type": "WebPage",
              "@id": `${URL_PAGINA}#pagina`,
              url: URL_PAGINA,
              name: TITOLO,
              description: DESCRIZIONE,
              inLanguage: "it-IT",
              primaryImageOfPage: { "@type": "ImageObject", url: `https://artigianidelcotto.com${dopo1.url}` },
              isPartOf: { "@type": "WebSite", "@id": "https://artigianidelcotto.com/#sito", url: "https://artigianidelcotto.com/", name: "Artigiani del Cotto" },
              breadcrumb: { "@id": `${URL_PAGINA}#briciole` },
            },
            {
              "@type": "Service",
              "@id": `${URL_PAGINA}#servizio`,
              name: "Trattamento cotto esterno",
              serviceType: "Lavaggio e trattamento protettivo di pavimenti in cotto esterni",
              category: "Manutenzione e trattamento pavimenti",
              description: DESCRIZIONE,
              url: URL_PAGINA,
              image: [
                `https://artigianidelcotto.com${dopo1.url}`,
                `https://artigianidelcotto.com${dopo3.url}`,
              ],
              provider: { "@id": "https://artigianidelcotto.com/#azienda" },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Lombardia" },
                ...province.map((p) => ({ "@type": "City", name: p })),
              ],
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              offers: {
                "@type": "Offer",
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: `${URL_PAGINA}#contatti`,
                description: "Sopralluogo e preventivo gratuiti in tutta la Lombardia.",
              },
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Interventi sul cotto esterno",
                itemListElement: interventi.map((s) => ({
                  "@type": "Offer",
                  itemOffered: { "@type": "Service", name: s.titolo, description: s.testo },
                })),
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${URL_PAGINA}#faq`,
              mainEntityOfPage: { "@id": `${URL_PAGINA}#pagina` },
              inLanguage: "it-IT",
              mainEntity: faq.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
            {
              "@type": "BreadcrumbList",
              "@id": `${URL_PAGINA}#briciole`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://artigianidelcotto.com/" },
                { "@type": "ListItem", position: 2, name: "Trattamento cotto esterno", item: URL_PAGINA },
              ],
            },
          ],
        }),
      },
    ],

  }),
});

function CottoEsterno() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />

      <main>
        <section className="gradient-warm py-16 text-background sm:py-20">
          <div className="mx-auto max-w-6xl px-5">
            <nav aria-label="Percorso" className="text-sm text-background/70">
              <Link to="/" className="underline-offset-4 hover:underline">Home</Link>
              <span className="px-2">/</span>
              <span>Trattamento cotto esterno</span>
            </nav>
            <p className="eyebrow mt-6 text-sand">Dal {ANNO_FONDAZIONE} in tutta la Lombardia</p>
            <h1 className="mt-4 max-w-3xl text-4xl leading-[1.08] font-semibold sm:text-5xl">
              Trattamento cotto esterno in Lombardia
            </h1>
            <p className="mt-5 max-w-2xl text-base text-background/90 sm:text-lg">
              Terrazze, cortili, scale, portici e bordi piscina: laviamo il cotto all'aperto e lo
              proteggiamo con prodotti idrorepellenti traspiranti, che tengono fuori acqua e gelo
              senza chiudere la mattonella. {ANNI_ESPERIENZA} di lavoro a mano, superficie per superficie.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#contatti"
                className="rounded-xs bg-copper px-6 py-3 text-sm font-semibold text-accent-foreground transition-transform hover:-translate-y-0.5"
              >
                Richiedi un preventivo gratuito
              </a>
              <a
                href={`tel:${TEL_ANTONIO}`}
                className="rounded-xs border border-background/40 px-6 py-3 text-sm font-semibold transition-colors hover:bg-background/10"
              >
                Chiama Antonio: 339 751 7813
              </a>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Il problema</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Il cotto all'aperto lavora tutto l'anno.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">
                Un pavimento esterno vive di pioggia, sbalzi termici e gelo. Se il cotto è poroso e
                non protetto, l'acqua entra: d'inverno congela, si espande e sfoglia la superficie.
                Nel frattempo compaiono muschio, aloni bianchi di sali (efflorescenze), macchie di
                foglie e uno scolorimento generale che fa sembrare vecchio anche un cortile nuovo.
              </p>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground">
                Per questo il trattamento da esterno è diverso da quello da interno: niente cere
                lucide, che all'aperto si sciolgono, ingialliscono e diventano scivolose, ma un
                protettivo che penetra e lascia respirare la mattonella.
              </p>
            </div>
            <ul className="surface-warm space-y-4 self-start rounded-sm border border-border p-7 text-sm">
              {[
                "Muschio e alghe nelle zone in ombra.",
                "Efflorescenze e aloni biancastri dopo la pioggia.",
                "Cotto che si sfoglia e si sbriciola dopo il gelo.",
                "Macchie di foglie, terra, vasi e ruggine.",
                "Colore spento e disomogeneo.",
              ].map((t) => (
                <li key={t} className="flex gap-3 border-b border-border pb-4 last:border-0 last:pb-0">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-copper" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="servizi-esterno" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Servizi per l'esterno</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Cosa facciamo su un cotto all'aperto.
            </h2>
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {interventi.map((s, i) => (
                <article
                  key={s.titolo}
                  className="surface-warm flex flex-col rounded-sm border border-border p-7 transition-transform hover:-translate-y-1"
                >
                  <span className="font-display text-sm font-semibold text-copper">0{i + 1}</span>
                  <h3 className="mt-3 text-xl font-semibold">{s.titolo}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.testo}</p>
                  <ul className="mt-6 space-y-2 border-t border-border pt-5 text-sm">
                    {s.voci.map((v) => (
                      <li key={v} className="flex gap-2">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-copper" />
                        <span>{v}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Come lavoriamo</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quattro passaggi, nessuna scorciatoia.
          </h2>
          <ol className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {passaggi.map((p, i) => (
              <li key={p.titolo} className="border-t-2 border-copper pt-5">
                <span className="font-display text-sm font-semibold text-copper">Fase {i + 1}</span>
                <h3 className="mt-2 text-lg font-semibold">{p.titolo}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{p.testo}</p>
              </li>
            ))}
          </ol>
        </section>

        <section id="lavori" className="scroll-mt-20 bg-secondary/40 py-20">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow">Prima e dopo</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
              Lavori in esterno: trascina il cursore.
            </h2>
            <div className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {lavoriEsterni.map((l) => (
                <BeforeAfter key={l.after} {...l} />
              ))}
            </div>
            <p className="eyebrow mt-16">Altri lavori in esterno</p>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {gallerie.map((g) => (
                <img
                  key={g.alt}
                  src={g.src}
                  alt={g.alt}
                  width={g.w}
                  height={g.h}
                  loading="lazy"
                  decoding="async"
                  className="aspect-4/3 w-full rounded-sm object-cover shadow-[var(--shadow-soft)]"
                />
              ))}
            </div>

          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-20">
          <p className="eyebrow">Domande frequenti</p>
          <h2 className="mt-3 max-w-2xl text-3xl font-semibold sm:text-4xl">
            Quello che ci chiedono più spesso.
          </h2>
          <div className="mt-10 divide-y divide-border border-y border-border">
            {faq.map((f) => (
              <details key={f.q} className="group py-5">
                <summary className="cursor-pointer list-none text-base font-semibold marker:hidden">
                  {f.q}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="gradient-warm py-16 text-background">
          <div className="mx-auto max-w-6xl px-5">
            <p className="eyebrow text-sand">Dove lavoriamo</p>
            <h2 className="mt-3 text-2xl font-semibold sm:text-3xl">
              Trattamento cotto esterno in tutta la Lombardia.
            </h2>
            <p className="mt-4 max-w-2xl text-background/85">
              Con sede a Torbole Casaglia (Brescia) interveniamo in tutte le province lombarde su
              cortili, terrazze, scale, portici e camminamenti.{" "}
              <Link to="/" className="underline underline-offset-4">
                Guarda tutti i nostri servizi
              </Link>
              .
            </p>
            <ul className="mt-8 flex flex-wrap gap-2">
              {province.map((p) => (
                <li
                  key={p}
                  className="rounded-xs border border-background/25 px-3 py-1.5 text-sm text-background/90"
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section id="contatti" className="mx-auto max-w-6xl scroll-mt-20 px-5 py-20">
          <div className="grid gap-12 md:grid-cols-2">
            <div>
              <p className="eyebrow">Preventivo</p>
              <h2 className="mt-3 text-3xl font-semibold sm:text-4xl">
                Mandaci due foto del tuo esterno.
              </h2>
              <p className="mt-4 text-muted-foreground">
                Bastano un paio di foto e i metri quadri indicativi per una prima stima.
                Sopralluogo e preventivo sono gratuiti e senza impegno.
              </p>
              <div className="mt-8 space-y-3">
                <a
                  href={`tel:${TEL_ANTONIO}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Antonio</span>
                  <span className="text-primary">+39 339 751 7813</span>
                </a>
                <a
                  href={`tel:${TEL_LUCA}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Luca</span>
                  <span className="text-primary">+39 351 720 4660</span>
                </a>
                <a
                  href={`mailto:${EMAIL}`}
                  className="surface-warm flex items-center justify-between rounded-sm border border-border px-5 py-4 transition-colors hover:border-primary"
                >
                  <span className="font-medium">Email</span>
                  <span className="text-primary">{EMAIL}</span>
                </a>
              </div>
              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex rounded-xs bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-sienna"
                >
                  Scrivi ad Antonio su WhatsApp
                </a>
                <SocialLinks />
              </div>
            </div>
            <PreventivoForm />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
